import { useWindowSize } from '@utils/hooks'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

export const withMobileGuard = <P extends object>(
  WrappedPage: React.ComponentType<P>,
  WrappedMobile?: React.ComponentType<P>,
) => {
  const WithMobileGuard = (props: P) => {
    const { isMobile } = useWindowSize()

    const { pathname } = useLocation()
    const { i18n } = useTranslation()
    const navigate = useNavigate()

    const { projectId, planId } = useParams()

    const supportedPaths = useMemo(
      () => [
        `/${i18n.language}/projects/${projectId}/view`,
        `/${i18n.language}/projects/${projectId}/plans`,
        `/${i18n.language}/projects/${projectId}/plans/editor/${planId}`,
        `/${i18n.language}/projects/${projectId}/documents`,
        `/${i18n.language}/projects/${projectId}/suivi`,
        `/${i18n.language}/projects/${projectId}/forms`,
        `/${i18n.language}/projects/${projectId}/observations`,
      ],
      [i18n.language, projectId, planId],
    )

    useEffect(() => {
      if (isMobile && projectId && supportedPaths.indexOf(pathname) == -1) {
        navigate(supportedPaths[0])
      }
    }, [isMobile, navigate, pathname, projectId, supportedPaths])

    const render = useMemo(
      () => (isMobile && WrappedMobile ? <WrappedMobile {...props} /> : <WrappedPage {...props} />),
      [isMobile, props],
    )

    return render
  }

  return WithMobileGuard
}
